import { useState } from 'react';
import { AlertTriangle, Check, X } from 'lucide-react';
import { api, ApiError, type SmsCandidateNumber, type SmsCandidateServer } from '../lib/api';
import { Button } from './ui/Button';
import { Select } from './ui/Select';
import { formatPhone } from '../lib/format';

/**
 * Both fields are pickers over server-supplied candidates, never text inputs.
 * A number typed by hand could be one AWS does not lease — which is the
 * `unleased` state that silently drops a customer onto the shared fallback — and
 * an address typed by hand could be a box that is not an X5 production server.
 * The API re-checks both, so this is the convenient path rather than the
 * boundary, but it is the path that should never be able to produce a bad row.
 */
export interface AssignmentTarget {
  /** Pre-selected number. Fixed when assigning a specific spare number. */
  phoneNumber?: string;
  /** Pre-selected server address. */
  serverIp?: string;
  /** The row being replaced, when this is a move rather than a new assignment. */
  replaces?: { serverIp: string; phoneNumber: string };
}

export function SmsAssignmentEditor({
  target,
  numbers,
  servers,
  currentServer,
  onCancel,
  onSaved,
}: {
  target: AssignmentTarget;
  numbers: SmsCandidateNumber[];
  servers: SmsCandidateServer[];
  /**
   * The server this assignment is already on. Candidates exclude anything
   * already assigned, so a move needs its own current server passed back in —
   * with its real name and instance id rather than a bare address.
   */
  currentServer?: SmsCandidateServer | undefined;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [phoneNumber, setPhoneNumber] = useState(target.phoneNumber ?? '');
  const [serverIp, setServerIp] = useState(target.serverIp ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A move keeps its current number and server selectable even though neither is
  // "free" — they are only taken by the row being replaced.
  const numberOptions = dedupeNumbers(numbers, target.phoneNumber);
  const serverOptions = dedupeServers(servers, target.serverIp, currentServer);
  const chosenServer = serverOptions.find((s) => s.serverIp === serverIp);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.putSmsAssignment({
        phone_number: phoneNumber,
        server_ip: serverIp,
        ...(target.replaces
          ? {
              replaces: {
                server_ip: target.replaces.serverIp,
                phone_number: target.replaces.phoneNumber,
              },
            }
          : {}),
      });
      onSaved();
    } catch (err) {
      // The API's rejections name the specific conflict ("already assigned to
      // x5-ghlabs"), so they are worth surfacing verbatim.
      setError(err instanceof ApiError ? err.message : (err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const ready = phoneNumber !== '' && serverIp !== '' && !saving;

  return (
    <div className="space-y-3 rounded-md border border-line bg-panel-2 p-3">
      <div className="space-y-1">
        <label className="text-xs text-muted-foreground" htmlFor="sms-number">
          Number
        </label>
        <Select
          id="sms-number"
          value={phoneNumber}
          onChange={(e) => setPhoneNumber(e.target.value)}
          disabled={saving}
        >
          <option value="">Select a leased number…</option>
          {numberOptions.map((n) => (
            <option key={n.phoneNumber} value={n.phoneNumber}>
              {formatPhone(n.phoneNumber)}
            </option>
          ))}
        </Select>
        <p className="text-xs text-muted-foreground">
          Only numbers this account leases with nothing sending from them.
        </p>
      </div>

      <div className="space-y-1">
        <label className="text-xs text-muted-foreground" htmlFor="sms-server">
          X5 server
        </label>
        <Select
          id="sms-server"
          value={serverIp}
          onChange={(e) => setServerIp(e.target.value)}
          disabled={saving}
        >
          <option value="">Select a server…</option>
          {serverOptions.map((s) => (
            <option key={s.serverIp} value={s.serverIp}>
              {s.label}
              {s.state !== 'running' ? ` — ${s.state}` : ''}
            </option>
          ))}
        </Select>
        <p className="text-xs text-muted-foreground">
          Tagged <span className="font-mono">Application=X5</span> and{' '}
          <span className="font-mono">Environment=production</span>, with no number of its own.
        </p>
      </div>

      {chosenServer && (
        <dl className="space-y-0.5 border-t border-line-soft pt-2 text-xs">
          <div className="flex justify-between gap-3">
            <dt className="text-muted-foreground">Registry label</dt>
            <dd className="font-mono">{chosenServer.label}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-muted-foreground">Instance</dt>
            <dd className="font-mono break-all">{chosenServer.instanceId}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-muted-foreground">Address</dt>
            <dd className="font-mono">{chosenServer.serverIp}</dd>
          </div>
        </dl>
      )}

      {chosenServer && chosenServer.state !== 'running' && (
        <p className="flex items-start gap-2 text-xs text-caution">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          That server is {chosenServer.state}. The assignment will be stored, but nothing sends until
          it is running.
        </p>
      )}

      {error && <p className="text-xs text-destructive">{error}</p>}

      <div className="flex items-center justify-end gap-2 pt-1">
        <Button variant="ghost" size="sm" onClick={onCancel} disabled={saving}>
          <X className="h-3.5 w-3.5" />
          Cancel
        </Button>
        <Button size="sm" onClick={() => void save()} disabled={!ready}>
          <Check className="h-3.5 w-3.5" />
          {saving ? 'Saving…' : 'Save assignment'}
        </Button>
      </div>
    </div>
  );
}

/**
 * The candidate lists deliberately exclude anything already assigned, so a move
 * has to have its own current selection added back or the field would open
 * blank and a save would look like a change when it is not.
 */
function dedupeNumbers(
  numbers: SmsCandidateNumber[],
  current: string | undefined,
): SmsCandidateNumber[] {
  const list = [...numbers];
  if (current && !list.some((n) => n.phoneNumber === current)) {
    list.unshift({ phoneNumber: current, numberType: '' });
  }
  return list;
}

function dedupeServers(
  servers: SmsCandidateServer[],
  current: string | undefined,
  currentServer: SmsCandidateServer | undefined,
): SmsCandidateServer[] {
  const list = [...servers];
  if (current && !list.some((s) => s.serverIp === current)) {
    list.unshift(
      currentServer ?? {
        instanceId: '',
        name: null,
        label: current,
        serverIp: current,
        state: 'running',
        availabilityZone: null,
      },
    );
  }
  return list;
}
